// src/pages/Journal.jsx
import React, { useState } from 'react';
import { Trash2, ExternalLink, ChevronDown, ChevronUp, AlertCircle, Edit2, X, ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const MISTAKES = ['None','Impatience','Revenge Trading','Overtrading','Adding to Loser','Chasing Entry','Broke SL','Broke TP','Sized Too Big','Overleveraged','FOMO','No Setup / No Confirmation','Other'];

function ResultTag({ result, status }) {
  if (status === 'open' || result === 'Ongoing') return <span style={{ display:'inline-flex', alignItems:'center', gap:4, fontSize:10, fontWeight:700, color:'#ffd166', background:'rgba(255,209,102,0.1)', border:'1px solid rgba(255,209,102,0.25)', borderRadius:4, padding:'2px 7px', fontFamily:"'DM Mono',monospace", letterSpacing:'0.06em' }}>OPEN</span>;
  if (result === 'Win')  return <span className="tag-win">WIN</span>;
  if (result === 'Loss') return <span className="tag-loss">LOSS</span>;
  return <span className="tag-be">BE</span>;
}

function RulesTag({ followed }) {
  const ok = followed === true || followed === 'true';
  return (
    <span style={{ fontSize:11, fontFamily:"'DM Mono',monospace", padding:'2px 8px', borderRadius:6, background:ok?'rgba(0,229,160,0.1)':'rgba(255,77,109,0.1)', color:ok?'#00e5a0':'#ff4d6d', border:`1px solid ${ok?'rgba(0,229,160,0.2)':'rgba(255,77,109,0.2)'}` }}>
      {ok ? '✓' : '✗'}
    </span>
  );
}

const tdS = { padding:'10px 12px', fontSize:13, color:'#8888a8', fontFamily:"'IBM Plex Sans',sans-serif", whiteSpace:'nowrap', verticalAlign:'middle' };

const ModalInput = ({ label, children }) => (
  <div>
    <label style={{ fontSize:11, color:'#8888a8', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', display:'block', marginBottom:5 }}>{label}</label>
    {children}
  </div>
);

const mInp = { width:'100%', background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:7, color:'#f0f0f5', padding:'9px 12px', fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:"'IBM Plex Sans',sans-serif" };

export default function Journal({ trades, onDelete, onUpdate, settings , lang = 'en'}) {
  const { user } = useAuth();

  // Sort / filter state
  const [sortField, setSortField] = useState('date');
  const [sortDir,   setSortDir]   = useState('desc');
  const [filter,    setFilter]    = useState('All');
  const [dateFrom,  setDateFrom]  = useState('');
  const [dateTo,    setDateTo]    = useState('');
  const [expanded,  setExpanded]  = useState(null);

  // Edit state
  const [editing,  setEditing]  = useState(null);
  const [editData, setEditData] = useState({});
  const [saving,   setSaving]   = useState(false);

  // Close trade state
  const [closing,     setClosing]     = useState(null);
  const [closeData,   setCloseData]   = useState({ pnl:'', result:'Win', rrAchieved:'', notes:'' });
  const [closingSave, setClosingSave] = useState(false);

  // Split open vs closed
  const openTrades   = trades.filter(t => t.status === 'open');
  const closedTrades = trades.filter(t => t.status !== 'open');

  const sorted = [...closedTrades]
    .filter(t => filter === 'All' || t.result === filter)
    .filter(t => !dateFrom || t.date >= dateFrom)
    .filter(t => !dateTo   || t.date <= dateTo)
    .sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (sortField === 'date') { av = new Date(av); bv = new Date(bv); }
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = field => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />;
  };

  // Edit handlers
  const startEdit = trade => {
    setEditing(trade.id);
    setEditData({
      pnl:           trade.pnl || 0,
      result:        trade.result || 'Win',
      rrAchieved:    trade.rrAchieved || '',
      stopLoss:      trade.stopLoss || '',
      takeProfit:    trade.takeProfit || '',
      mistakeMade:   trade.mistakeMade || 'None',
      screenshotLink:trade.screenshotLink || '',
      notes:         trade.notes || '',
    });
  };

  const saveEdit = async tradeId => {
    setSaving(true);
    await onUpdate(tradeId, {
      pnl:            Number(editData.pnl) || 0,
      result:         editData.result,
      rrAchieved:     Number(editData.rrAchieved) || null,
      stopLoss:       Number(editData.stopLoss) || null,
      takeProfit:     Number(editData.takeProfit) || null,
      mistakeMade:    editData.mistakeMade,
      screenshotLink: editData.screenshotLink,
      notes:          editData.notes,
    });
    setSaving(false);
    setEditing(null);
  };

  // Close trade handlers
  const startClose = trade => {
    setClosing(trade.id);
    setCloseData({ pnl:'', result:'Win', rrAchieved:'', notes:'' });
  };

  const saveClose = async tradeId => {
    setClosingSave(true);
    await onUpdate(tradeId, {
      status:     'closed',
      pnl:        Number(closeData.pnl) || 0,
      result:     closeData.result,
      rrAchieved: Number(closeData.rrAchieved) || null,
      notes:      closeData.notes,
    });
    setClosingSave(false);
    setClosing(null);
  };

  const colStyle = field => ({
    padding:'10px 12px', textAlign:'left', color:'#55556a',
    fontFamily:"'DM Mono',monospace", fontSize:10,
    textTransform:'uppercase', letterSpacing:'0.08em',
    cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
    background: sortField === field ? '#0d0d14' : 'transparent',
  });

  return (
    <div className="fade-in" style={{ padding:'28px 32px', maxWidth:1200, margin:'0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <h1 style={{ fontSize:22, fontWeight:600, color:'#f0f0f5', margin:0, fontFamily:"'IBM Plex Sans',sans-serif" }}>Trade Journal</h1>
        <p style={{ color:'#55556a', fontSize:13, margin:'4px 0 0', fontFamily:"'DM Mono',monospace" }}>
          {closedTrades.length} closed trade{closedTrades.length !== 1 ? 's' : ''}
          {openTrades.length > 0 && ` · ${openTrades.length} open`}
        </p>
      </div>

      {/* ── OPEN / ACTIVE TRADES ── */}
      {openTrades.length > 0 && (
        <div style={{ background:'rgba(255,209,102,0.05)', border:'1px solid rgba(255,209,102,0.2)', borderRadius:14, padding:20, marginBottom:24 }}>
          <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:'#ffd166', animation:'pulse 1.5s infinite' }} />
            <div style={{ fontSize:13, fontWeight:600, color:'#ffd166' }}>
              Open / Active Trades ({openTrades.length})
            </div>
            <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>
              Not included in closed-trade stats
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {openTrades.map(t => (
              <div key={t.id} style={{ background:'#16161f', border:'1px solid rgba(255,209,102,0.15)', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:14, flexWrap:'wrap' }}>
                <div style={{ fontFamily:"'DM Mono',monospace", fontSize:11, color:'#55556a' }}>{t.date}</div>
                <div style={{ fontSize:13, fontWeight:600, color:'#f0f0f5', minWidth:70 }}>{t.pair}</div>
                <div style={{ fontSize:12, color: t.direction==='Buy'?'#00e5a0':'#ff4d6d', fontWeight:500 }}>{t.direction}</div>
                <div style={{ fontSize:12, color:'#55556a' }}>Entry: <span style={{ color:'#8888a8' }}>{t.entryPrice || '—'}</span></div>
                <div style={{ fontSize:12, color:'#55556a' }}>SL: <span style={{ color:'#8888a8' }}>{t.stopLoss || '—'}</span></div>
                {t.session && <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>{t.session}</div>}
                <div style={{ flex:1 }} />
                <div style={{ display:'flex', gap:8 }}>
                  <button onClick={() => startClose(t)} style={{
                    background:'rgba(0,229,160,0.1)', border:'1px solid rgba(0,229,160,0.3)',
                    borderRadius:7, padding:'7px 16px', fontSize:12, color:'#00e5a0',
                    cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif", fontWeight:600,
                  }}>
                    Close Trade →
                  </button>
                  <button onClick={() => startEdit(t)} style={{
                    background:'none', border:'1px solid #1e1e2e', borderRadius:7,
                    padding:'7px 10px', cursor:'pointer', color:'#55556a',
                  }} title="Edit">
                    <Edit2 size={13} />
                  </button>
                  <button onClick={() => onDelete(t.id)} style={{
                    background:'none', border:'1px solid rgba(255,77,109,0.2)',
                    borderRadius:7, padding:'7px 10px', cursor:'pointer', color:'#ff4d6d',
                  }} title="Delete">
                    <X size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── FILTERS ── */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        {['All','Win','Loss','Breakeven'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding:'6px 14px', borderRadius:7, border:'1px solid', fontSize:12,
            cursor:'pointer', outline:'none', fontFamily:"'IBM Plex Sans',sans-serif",
            background: filter===f ? 'rgba(0,229,160,0.1)' : 'transparent',
            borderColor: filter===f ? 'rgba(0,229,160,0.3)' : '#1e1e2e',
            color: filter===f ? '#00e5a0' : '#8888a8',
          }}>{f}</button>
        ))}
        <div style={{ display:'flex', gap:6, marginLeft:'auto', alignItems:'center' }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:7, color:'#8888a8', padding:'6px 10px', fontSize:12, outline:'none' }} />
          <span style={{ color:'#55556a', fontSize:12 }}>→</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ background:'#0a0a0f', border:'1px solid #1e1e2e', borderRadius:7, color:'#8888a8', padding:'6px 10px', fontSize:12, outline:'none' }} />
        </div>
      </div>

      {/* ── TRADES TABLE ── */}
      {sorted.length === 0 ? (
        <div className="stat-card" style={{ padding:40, textAlign:'center' }}>
          <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
            <div style={{ width:48, height:48, borderRadius:12, background:'rgba(255,255,255,0.03)', border:'1px solid #1e1e2e', display:'flex', alignItems:'center', justifyContent:'center', color:'#55556a' }}>
              <ClipboardList size={22} strokeWidth={1.6} />
            </div>
          </div>
          <div style={{ fontSize:14, color:'#f0f0f5', fontWeight:500, marginBottom:6 }}>No trades yet</div>
          <div style={{ fontSize:13, color:'#55556a' }}>
            {trades.length === 0
              ? 'Start by logging your first trade using the New Trade button in the sidebar.'
              : 'No trades match the current filter.'}
          </div>
        </div>
      ) : (
        <div className="journal-table" style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:14, overflow:'hidden' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:'#0d0d14', borderBottom:'1px solid #1e1e2e' }}>
                  {[['date','Date'],['pair','Pair'],['direction','Dir'],['result','Result'],['pnl','P&L'],['rrAchieved','RR'],['followedRules','Rules']].map(([f,l]) => (
                    <th key={f} style={colStyle(f)} onClick={() => handleSort(f)}>
                      {l} <SortIcon field={f} />
                    </th>
                  ))}
                  <th style={{ ...colStyle(''), cursor:'default' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(t => (
                  <React.Fragment key={t.id}>
                    <tr
                      onClick={() => setExpanded(e => e === t.id ? null : t.id)}
                      style={{ borderTop:'1px solid #1e1e2e', cursor:'pointer', transition:'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#1a1a24'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={tdS}><span style={{ fontFamily:"'DM Mono',monospace", fontSize:11 }}>{t.date}</span></td>
                      <td style={{ ...tdS, color:'#f0f0f5', fontWeight:500 }}>{t.pair}</td>
                      <td style={{ ...tdS, color: t.direction==='Buy'?'#00e5a0':'#ff4d6d' }}>{t.direction}</td>
                      <td style={tdS}><ResultTag result={t.result} status={t.status} /></td>
                      <td style={{ ...tdS, color: Number(t.pnl)>=0?'#00e5a0':'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>
                        {Number(t.pnl)>=0?'+':''}${Number(t.pnl||0).toFixed(2)}
                      </td>
                      <td style={{ ...tdS, fontFamily:"'DM Mono',monospace" }}>{t.rrAchieved ? `${t.rrAchieved}R` : '—'}</td>
                      <td style={tdS}><RulesTag followed={t.followedRules} /></td>
                      <td style={{ ...tdS, display:'flex', gap:6, alignItems:'center' }}>
                        <button onClick={e => { e.stopPropagation(); startEdit(t); }}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a', padding:'4px', lineHeight:0, transition:'color 0.15s' }}
                          onMouseEnter={e=>e.currentTarget.style.color='#00e5a0'}
                          onMouseLeave={e=>e.currentTarget.style.color='#55556a'}
                          title="Edit trade">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={e => { e.stopPropagation(); if(window.confirm('Delete this trade?')) onDelete(t.id); }}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a', padding:'4px', lineHeight:0, transition:'color 0.15s' }}
                          onMouseEnter={e=>e.currentTarget.style.color='#ff4d6d'}
                          onMouseLeave={e=>e.currentTarget.style.color='#55556a'}
                          title="Delete trade">
                          <Trash2 size={13} />
                        </button>
                        {t.screenshotLink && (
                          <a href={t.screenshotLink} target="_blank" rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ color:'#55556a', lineHeight:0, transition:'color 0.15s' }}
                            onMouseEnter={e=>e.currentTarget.style.color='#4cc9f0'}
                            onMouseLeave={e=>e.currentTarget.style.color='#55556a'}>
                            <ExternalLink size={13} />
                          </a>
                        )}
                      </td>
                    </tr>

                    {/* Expanded row */}
                    {expanded === t.id && (
                      <tr style={{ background:'#0d0d14', borderTop:'1px solid #1e1e2e' }}>
                        <td colSpan={8} style={{ padding:'16px 20px' }}>
                          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(180px, 1fr))', gap:12 }}>
                            {[
                              ['Session',    t.session],
                              ['Entry',      t.entryPrice],
                              ['Stop Loss',  t.stopLoss],
                              ['Take Profit',t.takeProfit],
                              ['Lot Size',   t.lotSize],
                              ['Contracts',  t.contracts || null],
                              ['Risk $',     t.riskAmount ? `$${t.riskAmount}` : null],
                              ['Strategy',   t.poi],
                              ['Order Flow', t.orderFlow],
                              ['Timeframe',  t.analysisTF],
                              ['Emotion ↑',  t.emotionBefore],
                              ['Emotion ↓',  t.emotionAfter],
                              ['Mistake',    t.mistakeMade !== 'None' ? t.mistakeMade : null],
                              ['Source',     t.source !== 'manual' ? t.source?.toUpperCase() : null],
                            ].filter(([,v]) => v != null && v !== '' && v !== 0).map(([label, val]) => (
                              <div key={label}>
                                <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:3 }}>{label}</div>
                                <div style={{ fontSize:13, color:'#f0f0f5' }}>{val}</div>
                              </div>
                            ))}
                          </div>
                          {t.notes && (
                            <div style={{ marginTop:12, paddingTop:12, borderTop:'1px solid #1e1e2e' }}>
                              <div style={{ fontSize:10, color:'#55556a', fontFamily:"'DM Mono',monospace", textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:4 }}>Notes</div>
                              <div style={{ fontSize:13, color:'#8888a8', lineHeight:1.7 }}>{t.notes}</div>
                            </div>
                          )}
                          {t.mistakeMade && t.mistakeMade !== 'None' && (
                            <div style={{ marginTop:8, display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,77,109,0.06)', border:'1px solid rgba(255,77,109,0.15)', borderRadius:6, padding:'4px 10px' }}>
                              <AlertCircle size={11} style={{ color:'#ff4d6d' }} />
                              <span style={{ fontSize:11, color:'#ff4d6d' }}>{t.mistakeMade}</span>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── MOBILE CARD VIEW ── */}
      <div className="journal-cards" style={{ display:'none', flexDirection:'column', gap:10 }}>
        {sorted.map(t => (
          <div key={t.id} style={{ background:'#16161f', border:'1px solid #1e1e2e', borderRadius:12, padding:16 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:'#f0f0f5', marginBottom:2 }}>{t.pair}</div>
                <div style={{ fontSize:11, color:'#55556a', fontFamily:"'DM Mono',monospace" }}>{t.date} · {t.session || '—'}</div>
              </div>
              <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4 }}>
                <ResultTag result={t.result} status={t.status} />
                <span style={{ fontSize:13, fontWeight:600, color:Number(t.pnl)>=0?'#00e5a0':'#ff4d6d', fontFamily:"'DM Mono',monospace" }}>
                  {Number(t.pnl)>=0?'+':''}${Number(t.pnl||0).toFixed(2)}
                </span>
              </div>
            </div>
            <div style={{ display:'flex', gap:12, flexWrap:'wrap', fontSize:12, color:'#8888a8', marginBottom:10 }}>
              <span style={{ color:t.direction==='Buy'?'#00e5a0':'#ff4d6d', fontWeight:500 }}>{t.direction}</span>
              {t.rrAchieved && <span>{t.rrAchieved}R</span>}
              {t.session && <span>{t.session}</span>}
              <RulesTag followed={t.followedRules} />
            </div>
            {t.notes && <div style={{ fontSize:12, color:'#55556a', lineHeight:1.6, marginBottom:8 }}>{t.notes}</div>}
            <div style={{ display:'flex', gap:8, marginTop:6 }}>
              <button onClick={() => startEdit(t)} style={{ flex:1, background:'rgba(0,229,160,0.08)', border:'1px solid rgba(0,229,160,0.2)', borderRadius:7, padding:'7px', fontSize:12, color:'#00e5a0', cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Edit</button>
              <button onClick={() => { if(window.confirm('Delete?')) onDelete(t.id); }} style={{ flex:1, background:'rgba(255,77,109,0.06)', border:'1px solid rgba(255,77,109,0.15)', borderRadius:7, padding:'7px', fontSize:12, color:'#ff4d6d', cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {/* ── CLOSE TRADE MODAL ── */}
      {closing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div style={{ background:'#16161f', border:'1px solid #2a2a3e', borderRadius:16, padding:28, width:'100%', maxWidth:420 }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
              <div style={{ fontSize:15, fontWeight:600, color:'#f0f0f5' }}>Close Trade</div>
              <button onClick={() => setClosing(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a' }}><X size={18}/></button>
            </div>
            <div style={{ fontSize:13, color:'#8888a8', marginBottom:20 }}>Enter the final results to close this position.</div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <ModalInput label="Result">
                <select value={closeData.result} onChange={e => setCloseData(d => ({...d, result:e.target.value}))} style={mInp}>
                  {['Win','Loss','Breakeven'].map(o => <option key={o}>{o}</option>)}
                </select>
              </ModalInput>
              <ModalInput label="Final P&L ($)">
                <input type="number" step="any" value={closeData.pnl} onChange={e => setCloseData(d => ({...d, pnl:e.target.value}))} placeholder="e.g. 150.00" style={mInp} />
              </ModalInput>
              <ModalInput label="RR Achieved">
                <input type="number" step="any" value={closeData.rrAchieved} onChange={e => setCloseData(d => ({...d, rrAchieved:e.target.value}))} placeholder="e.g. 2.5" style={mInp} />
              </ModalInput>
              <ModalInput label="Notes">
                <textarea value={closeData.notes} onChange={e => setCloseData(d => ({...d, notes:e.target.value}))} rows={2} style={{ ...mInp, resize:'vertical' }} />
              </ModalInput>
              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                <button onClick={() => saveClose(closing)} disabled={closingSave} style={{ flex:2, background:'#00e5a0', color:'#0a0a0f', border:'none', borderRadius:8, padding:'12px', fontSize:13, fontWeight:700, cursor:closingSave?'not-allowed':'pointer', fontFamily:"'IBM Plex Sans',sans-serif", opacity:closingSave?0.7:1 }}>
                  {closingSave ? 'Saving...' : '✓ Close Trade'}
                </button>
                <button onClick={() => setClosing(null)} style={{ flex:1, background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:8, padding:'12px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT TRADE MODAL ── */}
      {editing && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
          <div style={{ background:'#16161f', border:'1px solid #2a2a3e', borderRadius:16, padding:28, width:'100%', maxWidth:480, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:15, fontWeight:600, color:'#f0f0f5' }}>Edit Trade</div>
              <button onClick={() => setEditing(null)} style={{ background:'none', border:'none', cursor:'pointer', color:'#55556a' }}><X size={18}/></button>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <ModalInput label="Result">
                <select value={editData.result||'Win'} onChange={e => setEditData(d => ({...d, result:e.target.value}))} style={mInp}>
                  {['Win','Loss','Breakeven'].map(o => <option key={o}>{o}</option>)}
                </select>
              </ModalInput>
              <ModalInput label="P&L ($)">
                <input type="number" step="any" value={editData.pnl||''} onChange={e => setEditData(d => ({...d, pnl:e.target.value}))} style={mInp} />
              </ModalInput>
              <ModalInput label="RR Achieved">
                <input type="number" step="any" value={editData.rrAchieved||''} onChange={e => setEditData(d => ({...d, rrAchieved:e.target.value}))} style={mInp} />
              </ModalInput>
              <ModalInput label="Stop Loss">
                <input type="number" step="any" value={editData.stopLoss||''} onChange={e => setEditData(d => ({...d, stopLoss:e.target.value}))} style={mInp} />
              </ModalInput>
              <ModalInput label="Take Profit">
                <input type="number" step="any" value={editData.takeProfit||''} onChange={e => setEditData(d => ({...d, takeProfit:e.target.value}))} style={mInp} />
              </ModalInput>
              <ModalInput label="Mistake">
                <select value={editData.mistakeMade||'None'} onChange={e => setEditData(d => ({...d, mistakeMade:e.target.value}))} style={mInp}>
                  {MISTAKES.map(o => <option key={o}>{o}</option>)}
                </select>
              </ModalInput>
              <ModalInput label="Screenshot Link">
                <input type="text" value={editData.screenshotLink||''} onChange={e => setEditData(d => ({...d, screenshotLink:e.target.value}))} placeholder="https://..." style={mInp} />
              </ModalInput>
              <ModalInput label="Notes">
                <textarea value={editData.notes||''} onChange={e => setEditData(d => ({...d, notes:e.target.value}))} rows={3} style={{ ...mInp, resize:'vertical' }} />
              </ModalInput>
              <div style={{ display:'flex', gap:10, marginTop:4 }}>
                <button onClick={() => saveEdit(editing)} disabled={saving} style={{ flex:2, background:'#00e5a0', color:'#0a0a0f', border:'none', borderRadius:8, padding:'12px', fontSize:13, fontWeight:700, cursor:saving?'not-allowed':'pointer', fontFamily:"'IBM Plex Sans',sans-serif", opacity:saving?0.7:1 }}>
                  {saving ? 'Saving...' : '✓ Save Changes'}
                </button>
                <button onClick={() => setEditing(null)} style={{ flex:1, background:'transparent', color:'#8888a8', border:'1px solid #1e1e2e', borderRadius:8, padding:'12px', fontSize:13, cursor:'pointer', fontFamily:"'IBM Plex Sans',sans-serif" }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>
    </div>
  );
}
